import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, Calendar, CheckCircle2, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    fullName: '',
    position: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    needsAppointment: false,
    appointmentDate: '',
    appointmentTime: '',
    zoomMeeting: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      if (response.data.success) {
        setSubmitted(true);
        setFormData({
          companyName: '',
          fullName: '',
          position: '',
          email: '',
          phone: '',
          serviceType: '',
          message: '',
          needsAppointment: false,
          appointmentDate: '',
          appointmentTime: '',
          zoomMeeting: false
        });
      }
    } catch (err) {
      console.error('Erreur lors de l\'envoi:', err);
      setError(
        err.response?.data?.detail || 
        'Une erreur s\'est produite. Veuillez réessayer ou nous contacter directement par téléphone.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: 'var(--bg-page)' }}>
      {/* Hero Section */}
      <section style={{
        padding: '180px 0 80px',
        background: 'var(--bg-page)',
        borderBottom: '1px solid var(--border-medium)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div className="caption" style={{
            color: 'var(--brand-primary)',
            marginBottom: '20px'
          }}>
            Contactez-nous
          </div>
          <h1 className="heading-1" style={{
            color: 'var(--brand-primary)',
            marginBottom: '30px',
            maxWidth: '800px'
          }}>
            Discutons de vos besoins
          </h1>
          <p className="body-large" style={{
            color: 'var(--text-secondary)',
            maxWidth: '700px'
          }}>
            Nous sommes disponibles 24h/24 – 7j/7 pour répondre à toutes vos questions.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section style={{
        padding: '100px 0',
        background: 'var(--bg-page)'
      }}>
        <div className="container" style={{
          maxWidth: '87.5rem',
          margin: '0 auto',
          padding: '0 40px'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '60px'
          }}>
            {/* Contact Information */}
            <div>
              <h2 className="heading-3" style={{
                color: 'var(--brand-primary)',
                marginBottom: '40px'
              }}>
                Nos coordonnées
              </h2>

              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '30px'
              }}>
                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    minWidth: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '50%'
                  }}>
                    <Mail size={24} color="var(--brand-primary)" />
                  </div>
                  <div>
                    <div className="heading-4" style={{
                      color: 'var(--brand-primary)',
                      marginBottom: '8px',
                      fontSize: '1rem'
                    }}>
                      Email
                    </div>
                    <a href="mailto:info@agence-cd.com" className="body-medium" style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none'
                    }}>
                      info@agence-cd.com
                    </a>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    minWidth: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '50%'
                  }}>
                    <Phone size={24} color="var(--brand-primary)" />
                  </div>
                  <div>
                    <div className="heading-4" style={{
                      color: 'var(--brand-primary)',
                      marginBottom: '8px',
                      fontSize: '1rem'
                    }}>
                      Téléphone
                    </div>
                    <a href="tel:+243841223949" className="body-medium" style={{
                      color: 'var(--text-secondary)',
                      textDecoration: 'none'
                    }}>
                      +243 841 223 949
                    </a>
                    <div className="caption" style={{
                      color: 'var(--text-muted)',
                      marginTop: '8px'
                    }}>
                      Disponible 24/7
                    </div>
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '20px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    minWidth: '50px',
                    height: '50px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '50%'
                  }}>
                    <MapPin size={24} color="var(--brand-primary)" />
                  </div>
                  <div>
                    <div className="heading-4" style={{
                      color: 'var(--brand-primary)',
                      marginBottom: '8px',
                      fontSize: '1rem'
                    }}>
                      Localisation
                    </div>
                    <p className="body-medium" style={{
                      color: 'var(--text-secondary)'
                    }}>
                      Kinshasa, RDC
                    </p>
                  </div>
                </div>
              </div>

              {/* Service Note */}
              <div style={{
                marginTop: '60px',
                padding: '30px',
                background: 'var(--secondary-olive)',
                border: '1px solid var(--border-medium)'
              }}>
                <div className="heading-4" style={{
                  color: 'var(--brand-primary)',
                  marginBottom: '15px',
                  fontSize: '1rem'
                }}>
                  Service Exclusif
                </div>
                <p className="body-small" style={{
                  color: 'var(--text-secondary)'
                }}>
                  Service réservé aux entreprises uniquement. Nombre limité de clients pour garantir une qualité irréprochable.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div style={{
                background: 'var(--bg-card)',
                padding: '40px',
                border: '1px solid var(--border-medium)'
              }}>
                {submitted ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px 20px'
                  }}>
                    <CheckCircle2 size={64} color="var(--brand-primary)" style={{ marginBottom: '20px' }} />
                    <h3 className="heading-3" style={{
                      color: 'var(--brand-primary)',
                      marginBottom: '20px'
                    }}>
                      Message envoyé !
                    </h3>
                    <p className="body-medium" style={{
                      color: 'var(--text-secondary)',
                      marginBottom: '30px'
                    }}>
                      Nous vous répondrons dans les plus brefs délais.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="btn-primary"
                    >
                      Envoyer un autre message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <h3 className="heading-3" style={{
                      color: 'var(--brand-primary)',
                      marginBottom: '30px'
                    }}>
                      Demander un devis
                    </h3>

                    {/* Error Message */}
                    {error && (
                      <div style={{
                        background: '#FEE2E2',
                        border: '1px solid #EF4444',
                        borderRadius: '8px',
                        padding: '16px',
                        marginBottom: '24px',
                        display: 'flex',
                        gap: '12px',
                        alignItems: 'flex-start'
                      }}>
                        <AlertCircle size={20} color="#DC2626" style={{ marginTop: '2px', flexShrink: 0 }} />
                        <p style={{
                          color: '#991B1B',
                          fontSize: '0.95rem',
                          margin: 0,
                          lineHeight: 1.5
                        }}>
                          {error}
                        </p>
                      </div>
                    )}

                    {/* Company Name */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Nom de l'entreprise *
                      </label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    {/* Full Name */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Nom complet *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    {/* Position */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Fonction
                      </label>
                      <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    {/* Email */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    {/* Phone */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Téléphone *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      />
                    </div>

                    {/* Service Type */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Type de service recherché *
                      </label>
                      <select
                        name="serviceType"
                        value={formData.serviceType}
                        onChange={handleChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none'
                        }}
                      >
                        <option value="">Sélectionnez un service</option>
                        <option value="billeterie">Billeterie Corporate</option>
                        <option value="affretement">Affrètement d'Avions</option>
                        <option value="voyages">Voyages d'Affaires Clés-en-Main</option>
                        <option value="evenements">Événements Corporate</option>
                        <option value="autre">Autre / Combinaison</option>
                      </select>
                    </div>

                    {/* Message */}
                    <div style={{ marginBottom: '20px' }}>
                      <label className="body-small" style={{
                        display: 'block',
                        color: 'var(--text-primary)',
                        marginBottom: '8px',
                        fontWeight: 600
                      }}>
                        Message
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: 'var(--bg-page)',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '4px',
                          color: 'var(--text-primary)',
                          fontSize: '1rem',
                          outline: 'none',
                          resize: 'vertical'
                        }}
                      />
                    </div>

                    {/* Appointment Section */}
                    <div style={{
                      marginBottom: '20px',
                      padding: '20px',
                      background: 'var(--secondary-olive)',
                      border: '1px solid var(--border-medium)',
                      borderRadius: '4px'
                    }}>
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          cursor: 'pointer'
                        }}>
                          <input
                            type="checkbox"
                            name="needsAppointment"
                            checked={formData.needsAppointment}
                            onChange={handleChange}
                            style={{
                              width: '20px',
                              height: '20px',
                              cursor: 'pointer'
                            }}
                          />
                          <span className="body-small" style={{
                            color: 'var(--text-primary)',
                            fontWeight: 600
                          }}>
                            <Calendar size={18} style={{ display: 'inline', marginRight: '8px' }} />
                            Je souhaite proposer un rendez-vous
                          </span>
                        </label>
                      </div>

                      {formData.needsAppointment && (
                        <>
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '15px',
                            marginBottom: '15px'
                          }}>
                            <div>
                              <label className="body-small" style={{
                                display: 'block',
                                color: 'var(--text-primary)',
                                marginBottom: '8px'
                              }}>
                                Date proposée
                              </label>
                              <input
                                type="date"
                                name="appointmentDate"
                                value={formData.appointmentDate}
                                onChange={handleChange}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  background: 'var(--bg-page)',
                                  border: '1px solid var(--border-medium)',
                                  borderRadius: '4px',
                                  color: 'var(--text-primary)',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                            <div>
                              <label className="body-small" style={{
                                display: 'block',
                                color: 'var(--text-primary)',
                                marginBottom: '8px'
                              }}>
                                Heure proposée
                              </label>
                              <input
                                type="time"
                                name="appointmentTime"
                                value={formData.appointmentTime}
                                onChange={handleChange}
                                style={{
                                  width: '100%',
                                  padding: '10px',
                                  background: 'var(--bg-page)',
                                  border: '1px solid var(--border-medium)',
                                  borderRadius: '4px',
                                  color: 'var(--text-primary)',
                                  fontSize: '0.95rem'
                                }}
                              />
                            </div>
                          </div>

                          <label style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            cursor: 'pointer'
                          }}>
                            <input
                              type="checkbox"
                              name="zoomMeeting"
                              checked={formData.zoomMeeting}
                              onChange={handleChange}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                            />
                            <span className="body-small" style={{
                              color: 'var(--text-secondary)'
                            }}>
                              Réunion Zoom souhaitée
                            </span>
                          </label>
                        </>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="btn-primary"
                      disabled={loading}
                      style={{
                        width: '100%',
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {loading ? 'Envoi en cours...' : 'Envoyer la demande'} <Send size={20} />
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
